<template>
  <div class="view-container">
    <div class="view-header">
      <div>
        <h1 class="view-title">Sieve 郵件過濾規則</h1>
        <p class="view-subtitle">為使用者設定個人化郵件過濾規則，自動分類、標記或拒絕郵件</p>
      </div>
    </div>

    <div class="view-body space-y-4">

      <!-- User selector (admin only) -->
      <div v-if="isSuperAdmin || isDomainAdmin" class="card p-4 flex items-center gap-3">
        <label class="text-sm font-medium" style="color:var(--gray-700);white-space:nowrap;">選擇使用者</label>
        <select v-model="sieve.selectedUserId" @change="onUserChange" class="input" style="max-width:320px;">
          <option v-for="u in sieve.users" :key="u.id" :value="u.id">{{ u.email }}</option>
        </select>
        <span v-if="sieve.loading" class="text-sm" style="color:var(--gray-500);">載入中…</span>
      </div>

      <!-- Global enable toggle + save -->
      <div class="card p-4 flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <button type="button" @click="sieve.enabled = !sieve.enabled"
            class="btn btn-secondary btn-sm"
            :style="sieve.enabled ? 'background:var(--brand-500);color:#fff;border-color:var(--brand-500);' : ''">
            {{ sieve.enabled ? '已啟用' : '已停用' }}
          </button>
          <span class="text-sm" style="color:var(--gray-600);">
            {{ sieve.enabled ? '過濾規則已啟用，郵件送達時會套用以下規則' : '過濾規則已停用，所有郵件照常投遞' }}
          </span>
        </div>
        <button type="button" @click="saveSieveRules" :disabled="sieve.saving || !sieve.selectedUserId"
          class="btn btn-primary btn-sm">
          {{ sieve.saving ? '儲存中…' : '儲存規則' }}
        </button>
      </div>

      <!-- Rules list -->
      <div class="card p-4 space-y-3">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold" style="color:var(--gray-800);">過濾規則列表</h3>
          <button type="button" @click="addRule" class="btn btn-secondary btn-sm">+ 新增規則</button>
        </div>

        <div v-if="!sieve.rules.length" class="py-8 text-center text-sm" style="color:var(--gray-400);">
          尚未建立任何規則，點擊「新增規則」開始設定
        </div>

        <!-- Rule cards -->
        <div v-for="(rule, ri) in sieve.rules" :key="ri" class="card p-4 space-y-3" style="border:1px solid var(--border);">
          <!-- Rule header -->
          <div class="flex items-center gap-2">
            <input v-model="rule.name" class="input flex-1" placeholder="規則名稱（選填）" />
            <button type="button" @click="rule.enabled = !rule.enabled"
              class="btn btn-secondary btn-xs"
              :style="rule.enabled ? 'background:var(--brand-500);color:#fff;border-color:var(--brand-500);' : ''">
              {{ rule.enabled ? '啟用' : '停用' }}
            </button>
            <button type="button" @click="moveRule(ri, -1)" :disabled="ri === 0" class="btn btn-ghost btn-xs">↑</button>
            <button type="button" @click="moveRule(ri, 1)" :disabled="ri === sieve.rules.length - 1" class="btn btn-ghost btn-xs">↓</button>
            <button type="button" @click="removeRule(ri)" class="btn btn-ghost btn-xs" style="color:var(--error);">✕</button>
          </div>

          <!-- Condition match mode -->
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium" style="color:var(--gray-600);">符合條件：</span>
            <select v-model="rule.condition_match" class="input input-sm" style="width:auto;">
              <option value="all">全部（AND）</option>
              <option value="any">任一（OR）</option>
            </select>
          </div>

          <!-- Conditions -->
          <div class="space-y-2">
            <div class="text-xs font-medium" style="color:var(--gray-600);">條件</div>
            <div v-for="(cond, ci) in rule.conditions" :key="ci" class="flex items-center gap-2 flex-wrap">
              <select v-model="cond.field" class="input input-sm" style="width:130px;">
                <option value="from">寄件人 (From)</option>
                <option value="to">收件人 (To)</option>
                <option value="subject">主旨</option>
                <option value="body">內容</option>
                <option value="size">郵件大小</option>
                <option value="has_attachment">附件</option>
              </select>
              <select v-model="cond.operator" class="input input-sm" style="width:130px;">
                <template v-if="cond.field === 'size'">
                  <option value="over">大於 (bytes)</option>
                  <option value="under">小於 (bytes)</option>
                </template>
                <template v-else-if="cond.field === 'has_attachment'">
                  <option value="is">是</option>
                  <option value="is_not">否</option>
                </template>
                <template v-else>
                  <option value="contains">包含</option>
                  <option value="not_contains">不包含</option>
                  <option value="is">完全符合</option>
                  <option value="starts_with">開頭為</option>
                </template>
              </select>
              <input v-if="cond.field !== 'has_attachment'" v-model="cond.value" class="input input-sm flex-1"
                :placeholder="cond.field === 'size' ? '位元組數，例如 1048576' : '輸入比對值'" />
              <button type="button" @click="removeCondition(ri, ci)" class="btn btn-ghost btn-xs" style="color:var(--error);">✕</button>
            </div>
            <button type="button" @click="addCondition(ri)" class="btn btn-ghost btn-xs" style="color:var(--brand-600);">+ 新增條件</button>
          </div>

          <!-- Actions -->
          <div class="space-y-2">
            <div class="text-xs font-medium" style="color:var(--gray-600);">動作</div>
            <div v-for="(act, ai) in rule.actions" :key="ai" class="flex items-center gap-2 flex-wrap">
              <select v-model="act.type" class="input input-sm" style="width:160px;">
                <option value="fileinto">移至資料夾</option>
                <option value="discard">靜默丟棄</option>
                <option value="reject">拒絕並回覆</option>
                <option value="redirect">轉寄至</option>
                <option value="mark_read">標記已讀</option>
                <option value="mark_flagged">標記旗標</option>
                <option value="stop">停止處理</option>
              </select>
              <input v-if="act.type === 'fileinto'" v-model="act.folder" class="input input-sm flex-1" placeholder="資料夾名稱，例如 Junk" />
              <input v-else-if="act.type === 'reject'" v-model="act.message" class="input input-sm flex-1" placeholder="拒絕原因訊息（選填）" />
              <input v-else-if="act.type === 'redirect'" v-model="act.address" class="input input-sm flex-1" placeholder="轉寄目標 Email" />
              <button type="button" @click="removeAction(ri, ai)" class="btn btn-ghost btn-xs" style="color:var(--error);">✕</button>
            </div>
            <button type="button" @click="addAction(ri)" class="btn btn-ghost btn-xs" style="color:var(--brand-600);">+ 新增動作</button>
          </div>
        </div>
      </div>

      <!-- Script preview -->
      <div class="card p-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold" style="color:var(--gray-800);">Sieve 腳本預覽</h3>
          <button type="button" @click="fetchSieveScript()" class="btn btn-secondary btn-xs">重新整理</button>
        </div>
        <div v-if="sieve.scriptLoading" class="text-sm py-3" style="color:var(--gray-400);">載入中…</div>
        <pre v-else-if="sieve.scriptPreview" class="text-xs p-3 rounded overflow-auto" style="background:var(--gray-50);border:1px solid var(--border);color:var(--gray-700);max-height:320px;white-space:pre-wrap;font-family:monospace;">{{ sieve.scriptPreview }}</pre>
        <div v-else class="text-sm py-3" style="color:var(--gray-400);">尚無腳本，儲存規則後將自動生成</div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()
const { sieve, isSuperAdmin, isDomainAdmin } = storeToRefs(store)
const { fetchSieveUsers, fetchSieveRules, saveSieveRules, fetchSieveScript } = store

onMounted(async () => {
  if (isSuperAdmin.value || isDomainAdmin.value) {
    await fetchSieveUsers()
  } else {
    // regular user: use own user id
    const me = store.currentUser
    if (me?.id) {
      sieve.value.selectedUserId = me.id
    }
  }
  if (sieve.value.selectedUserId) {
    await fetchSieveRules()
    await fetchSieveScript()
  }
})

async function onUserChange() {
  await fetchSieveRules()
  await fetchSieveScript()
}

function addRule() {
  sieve.value.rules.push({
    name: '',
    enabled: true,
    condition_match: 'all',
    conditions: [{ field: 'from', operator: 'contains', value: '' }],
    actions: [{ type: 'fileinto', folder: '' }],
  })
}

function removeRule(ri) {
  sieve.value.rules.splice(ri, 1)
}

function moveRule(ri, dir) {
  const rules = sieve.value.rules
  const to = ri + dir
  if (to < 0 || to >= rules.length) return
  const tmp = rules[ri]
  rules[ri] = rules[to]
  rules[to] = tmp
}

function addCondition(ri) {
  sieve.value.rules[ri].conditions.push({ field: 'from', operator: 'contains', value: '' })
}

function removeCondition(ri, ci) {
  sieve.value.rules[ri].conditions.splice(ci, 1)
}

function addAction(ri) {
  sieve.value.rules[ri].actions.push({ type: 'fileinto', folder: '' })
}

function removeAction(ri, ai) {
  sieve.value.rules[ri].actions.splice(ai, 1)
}
</script>
